import { useTranslation } from 'react-i18next';
import { Button } from '@parrot-software/design-system';
import { FileInput as FileInputIcon, Loader2 } from '@parrot-software/design-system/icons';

interface ReportExportButtonProps {
  onClick?: () => void;
  disabled?: boolean;
  isLoading?: boolean;
}

/**
 * ReportExportButton - Export to Excel button
 *
 * Displays a button to export the report to Excel format.
 * Shows loading state with spinner during export.
 *
 * @param {ReportExportButtonProps} props - Component properties
 * @param {Function} [props.onClick] - Callback when button is clicked
 * @param {boolean} [props.disabled=false] - Whether the button is disabled
 * @param {boolean} [props.isLoading=false] - Whether export is in progress
 * @returns {React.ReactElement} Export button component
 *
 * @example
 * <ReportExportButton
 *   onClick={handleExport}
 *   disabled={!hasData}
 *   isLoading={isExporting}
 * />
 */
export const ReportExportButton = ({ onClick, disabled = false, isLoading = false }: ReportExportButtonProps) => {
  const { t } = useTranslation('reports');

  return (
    <Button className="flex items-center gap-2 w-full md:w-auto" disabled={disabled || isLoading} onClick={onClick}>
      {isLoading ? (
        <>
          <Loader2 className="animate-spin" size={16} />
          {t('reportCommon:buttons.exporting')}
        </>
      ) : (
        <>
          <FileInputIcon size={16} />
          {t('reportCommon:buttons.exportToExcel')}
        </>
      )}
    </Button>
  );
};
